// @ts-nocheck
// client/src/v2/note-card.tsx
// O CARD DE UMA NOTA — usado pelo caderno do livro E pela visão global.
//
// Por que saiu de book-study.tsx: ele morava no mesmo arquivo da tela do
// livro, e a visão global importava dali. O efeito era que abrir "Todas as
// notas" arrastava o BookStudyScreen inteiro para o bundle — com o composer,
// a câmera e o gravador — para desenhar cards. Com a reforma dos cadernos
// surge um terceiro consumidor, e três telas dependendo do arquivo mais
// pesado do subsistema seria pior ainda.
//
// O card recebe a nota JÁ PRONTA e um `pageLabel` injetado: ele não sabe a
// unidade do livro (páginas, capítulos, posições) nem deve saber. Quem chama
// é que traduz.

import React, { useState } from 'react';
import { OverlayStoryModal, BotaoOverlayStory } from './overlay-story';
import { AudioPlayer, fmtTime } from './audio';
import { textoPuro } from './doc';

export const TYPECOLOR: Record<string, string> = { nota: 'var(--ink-3)', grifo: 'var(--ember)', ideia: 'var(--sage)', pergunta: 'var(--plum)', audio: 'var(--ember)' };
// O corpo que o card mostra. A folha do caderno pessoal guarda um documento;
// aqui ele aparece como texto, nunca com marcação à mostra.
const corpoDe = (n: any) => textoPuro(n);

/** O trecho procurado, aceso dentro do texto.
 *
 *  Vive aqui, e não na tela dos Cadernos, porque o card é o mesmo nos três
 *  lugares que listam nota. Sem `busca`, devolve o texto igualzinho — as telas
 *  que não procuram nada não mudam em nada. */
export function Destacado({ texto, busca }: { texto: string; busca?: string }) {
  const alvo = semAcento(busca);
  if (!alvo) return <>{texto}</>;
  const i = semAcento(texto).indexOf(alvo);
  if (i < 0) return <>{texto}</>;
  return (
    <>
      {texto.slice(0, i)}
      <mark style={{ background: 'var(--ember-soft)', color: 'var(--ember-2)', borderRadius: 3, padding: '0 2px' }}>
        {texto.slice(i, i + alvo.length)}
      </mark>
      {texto.slice(i + alvo.length)}
    </>
  );
}

export const LABEL: Record<string, string> = { nota: 'NOTA', grifo: 'GRIFO', ideia: 'IDEIA', pergunta: 'PERGUNTA', audio: 'ÁUDIO' };
export const COLORVAR: Record<string, string> = { ember: 'var(--ember)', sage: 'var(--sage)', plum: 'var(--plum)' };

export const FILTERS = [
  { key: 'tudo', label: 'Tudo' }, { key: 'grifo', label: 'Grifos' }, { key: 'ideia', label: 'Ideias' },
  { key: 'pergunta', label: 'Perguntas' }, { key: 'foto', label: 'Fotos' }, { key: 'audio', label: 'Áudios' },
];

/** Sem acento e sem caixa, dos DOIS lados da comparação — espelha
 *  `semAcento` de server/cadernos/routes.js. Sem isto "atomicos" não acha
 *  "Hábitos Atômicos", e o comentário da busca afirmava que achava. */
export function semAcento(s: any): string {
  return String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/** Rótulo da âncora conforme a unidade de progresso do livro.
 *  A versão antiga testava 'percent', 'locations' e 'loc' — três unidades que
 *  NÃO existem em ProgressUnit — e deixava passar as duas reais. Um livro em
 *  capítulos mostrava "p. 12". */
export function makePageLabel(unit?: string) {
  return (n: number | null) => {
    if (n === null || n === undefined) return 'avulsa';
    if (unit === 'chapters') return `cap. ${n}`;
    if (unit === 'positions') return `pos. ${n}`;
    return `p. ${n}`;
  };
}

const botaoRodape: React.CSSProperties = {
  width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'transparent',
  color: 'var(--ink-4)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', padding: 0,
};

export function NoteCard({ n, justAdded, onEdit, pageLabel, livro, busca }: {
  n: any; justAdded: boolean; onEdit: () => void; pageLabel: (p: number | null) => string;
  /** O livro da nota. Sem ele não há o que carimbar no adesivo — some o botão. */
  livro?: { titulo: string; autor?: string | null; capaUrl?: string | null } | null;
  /** O que está sendo procurado, para acender o trecho. Opcional: sem ele o
   *  card desenha exatamente como sempre desenhou. */
  busca?: string;
}) {
  // O TÍTULO SAI JUNTO DO CORPO, e não num sétimo lugar.
  //
  // O card tem seis ramos e nenhum invólucro comum — foi assim que o selo
  // "para memorizar" acabou existindo em só um deles. Pendurando o título
  // aqui, ele aparece nos seis de uma vez, e herda a tipografia do ramo: na
  // foto sai em serifada como a legenda, no grifo sai como grifo.
  //
  // Antes, o título só era mostrado pela lista dos Cadernos, que desenhava um
  // card próprio. Ao passar essa lista a usar este componente, o título teria
  // simplesmente sumido.
  const Corpo = () => (
    <>
      {n.title && (
        <span style={{ display: 'block', fontWeight: 700, marginBottom: 3 }}>
          <Destacado texto={n.title} busca={busca} />
        </span>
      )}
      <Destacado texto={corpoDe(n)} busca={busca} />
    </>
  );
  const glow = justAdded ? '0 0 0 2px var(--ember)' : 'none';
  const [overlayAberto, setOverlayAberto] = useState(false);
  const typeLabel = (LABEL[n.type] || 'NOTA') + (n.photo ? ' · FOTO' : '');
  const hasColor = n.color && n.color !== 'none';
  const dot = hasColor && <span style={{ width: 8, height: 8, borderRadius: '50%', background: COLORVAR[n.color] || 'transparent' }} />;
  const date = n.createdAt ? new Date(n.createdAt).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' }) : '';
  const memo = n.memorize && <span style={{ marginLeft: 'auto', fontFamily: 'Geist Mono, monospace', fontSize: 8.5, letterSpacing: '0.06em', color: 'var(--ember)', border: '1px solid var(--ember)', borderRadius: 20, padding: '2px 8px', opacity: 0.85 }}>PARA MEMORIZAR</span>;

  // Só faz sentido carimbar o que tem texto — nota de voz sem transcrição
  // viraria um adesivo vazio.
  const podeCompartilhar = !!livro && !!corpoDe(n).trim();

  // TUDO NO RODAPÉ — 24/09/2026.
  //
  // Antes o compartilhar e o ⋮ ficavam ABSOLUTOS no canto superior direito,
  // por cima do texto: a primeira linha da nota passava por baixo deles, e
  // por isso existia um `recuo` empurrando o parágrafo inteiro. E a página
  // morava na espinha, colada na linha vertical, ilegível.
  //
  // Agora os seis ramos usam ESTE rodapé — tipo · página · data à esquerda,
  // ações à direita. Um lugar só significa que não dá mais para um ramo
  // divergir do outro, que foi exatamente como o selo "para memorizar"
  // acabou existindo em só um dos seis.
  const pagina = n.page != null ? pageLabel(n.page) : null;

  // A LETRA DA NOTA. `font` nulo = herda a aparência do tipo, que é como as
  // 49 notas anteriores a 24/09/2026 sempre foram desenhadas. Escolhida,
  // ela manda — o tipo deixou de decidir aparência.
  const LETRA = {
    sans:  { familia: 'inherit', tamanho: 15.5, altura: 1.62 },
    serif: { familia: 'Instrument Serif, Georgia, serif', tamanho: 19, altura: 1.4 },
    mono:  { familia: 'Geist Mono, monospace', tamanho: 13.5, altura: 1.6 },
  };
  const letra = n.font ? LETRA[n.font] : null;

  const acoes = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginLeft: 'auto' }}>
      {podeCompartilhar && (
        <button onClick={() => setOverlayAberto(true)} aria-label="Compartilhar" style={botaoRodape}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
            <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
          </svg>
        </button>
      )}
      <button onClick={onEdit} aria-label="Opções" style={botaoRodape}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
      </button>
    </span>
  );

  /** O rodapé de qualquer nota. `tom` existe porque o polaroide da foto tem
   *  papel próprio, fora das paletas. */
  const rodape = (rotulo: string, tom?: { texto: string; borda: string }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
      <span className="mono" style={{ fontSize: 9, letterSpacing: '0.1em', color: tom ? tom.texto : 'var(--ink-4)' }}>{rotulo}</span>
      {pagina && (
        <span className="mono" style={{ fontSize: 9, color: tom ? tom.texto : 'var(--ink-4)', opacity: 0.8 }}>{pagina}</span>
      )}
      <span className="mono" style={{ fontSize: 9, color: tom ? tom.texto : 'var(--ink-4)', opacity: 0.8 }}>{date}</span>
      {dot}
      {n.memorize && (
        <span className="mono" style={{
          fontSize: 8, letterSpacing: '0.06em', padding: '2px 7px', borderRadius: 20,
          color: tom ? tom.texto : 'var(--ember)',
          border: `1px solid ${tom ? tom.borda : 'var(--ember)'}`,
        }}>MEMORIZAR</span>
      )}
      {!tom && acoes}
    </div>
  );

  const modalStory = podeCompartilhar ? (
    <OverlayStoryModal
      aberto={overlayAberto}
      onFechar={() => setOverlayAberto(false)}
      dados={{
        tipo: 'nota',
        titulo: livro!.titulo,
        autor: livro!.autor,
        capaUrl: livro!.capaUrl,
        texto: corpoDe(n),
        rotulo: LABEL[n.type] || 'NOTA',
        pagina,
      }}
    />
  ) : null;

  // FOTO / citação
  if (n.photo) {
    return (
      <>
        {modalStory}
        <div style={{ background: '#f4efe4', padding: '9px 9px 0', borderRadius: 3, boxShadow: justAdded ? '0 0 0 2px var(--ember), 0 14px 30px rgba(0,0,0,.4)' : '0 14px 30px rgba(0,0,0,.4)', transform: 'rotate(-1.6deg)', maxWidth: 248, animation: justAdded ? 'bsDrop .5s ease' : undefined }}>
          <img src={n.photoUrl} alt="" style={{ width: '100%', display: 'block', borderRadius: 1, maxHeight: 240, objectFit: 'cover' }} />
          <div style={{ padding: '11px 4px 13px' }}>
            {(corpoDe(n) || n.title) && <div style={{ fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: 15.5, lineHeight: 1.3, color: '#2a2118' }}>{<Corpo />}</div>}
            {/* Cor literal de propósito: o polaroide tem papel próprio, fora
                das paletas. */}
            {rodape(typeLabel, { texto: '#8a7048', borda: '#c7ad7d' })}
          </div>
        </div>
      </>
    );
  }

  // ÁUDIO
  if (n.audio) {
    return (
      <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderLeft: '3px solid var(--ember)', borderRadius: '4px 12px 12px 4px', padding: '13px 15px', boxShadow: glow, position: 'relative' }}>
        {modalStory}
        {(corpoDe(n) || n.title)
          ? <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 18, lineHeight: 1.32, color: 'var(--ink)', marginBottom: 12, paddingRight: 24 }}>{<Corpo />}</div>
          : <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11, letterSpacing: '0.04em', color: 'var(--ink-3)', marginBottom: 10 }}>Nota de voz · {fmtTime(n.audioDur)}</div>}
        <AudioPlayer url={n.audioUrl} dur={n.audioDur || 1} wave={n.wave} />
        {rodape('ÁUDIO')}
      </div>
    );
  }

  // GRIFO (texto)
  if (n.type === 'grifo') {
    return (
      <div style={{ borderRadius: 6, boxShadow: glow, position: 'relative' }}>
        {modalStory}
        <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 22, lineHeight: 1.3, color: 'var(--ink)', paddingRight: 22 }}>
          <span style={{ background: 'var(--ember-soft)', WebkitBoxDecorationBreak: 'clone', boxDecorationBreak: 'clone', padding: '1px 4px', borderRadius: 2 }}>“{<Corpo />}”</span>
        </div>
        {rodape('GRIFO')}
      </div>
    );
  }

  // IDEIA
  if (n.type === 'ideia') {
    return (
      <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderLeft: '3px solid var(--sage)', borderRadius: '4px 12px 12px 4px', padding: '14px 16px', boxShadow: glow, position: 'relative' }}>
        {modalStory}
        <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.14em', color: 'var(--sage)', marginBottom: 8 }}>IDEIA</div>
        <div style={{ fontSize: 15, lineHeight: 1.55, color: 'var(--ink-2)', paddingRight: 18 }}>{<Corpo />}</div>
        {rodape('IDEIA')}
      </div>
    );
  }

  // PERGUNTA
  if (n.type === 'pergunta') {
    return (
      <div style={{ background: 'var(--paper-2)', border: '1px solid var(--line)', borderLeft: '3px solid var(--plum)', borderRadius: '4px 12px 12px 4px', padding: '14px 16px', boxShadow: glow, position: 'relative' }}>
        {modalStory}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontFamily: 'Instrument Serif, serif', fontSize: 22, lineHeight: 0.6, color: 'var(--plum)' }}>?</span>
          <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.14em', color: 'var(--plum)' }}>PERGUNTA</span>
        </div>
        <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 18, lineHeight: 1.34, color: 'var(--ink)', paddingRight: 18 }}>{<Corpo />}</div>
        {rodape('PERGUNTA')}
      </div>
    );
  }

  // NOTA LIVRE
  return (
    <div style={{ borderRadius: 6, boxShadow: glow, position: 'relative' }}>
      {modalStory}
      <div style={{
        fontSize: letra ? letra.tamanho : 15.5,
        lineHeight: letra ? letra.altura : 1.62,
        fontFamily: letra ? letra.familia : 'inherit',
        color: 'var(--ink-2)', paddingRight: 22, whiteSpace: 'pre-wrap',
      }}>{<Corpo />}</div>
      {rodape('NOTA')}
    </div>
  );
}
